export const ENTRY_TYPES = ["Standard hours", "Overtime", "Leave"];
export const LEAVE_TYPES = ["Leave", "Holiday", "Sick leave"];

const TYPE_BUCKET = {
  "Standard hours": "standard",
  Overtime: "overtime",
  Leave: "leave",
  Holiday: "leave",
  "Sick leave": "leave",
};

function toISO(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function mondayOf(dLike) {
  const d = new Date(typeof dLike === "string" ? dLike + "T00:00:00" : dLike);
  const off = (d.getDay() + 6) % 7; // 0=Mon
  d.setDate(d.getDate() - off);
  return toISO(d);
}

function monthLast(iso) {
  const [y, m] = iso.split("-").map(Number);
  return toISO(new Date(y, m, 0));
}

// The week a date belongs to ends on Friday, or on the month's last day if that comes first
export function periodEnd(dLike) {
  const iso = typeof dLike === "string" ? dLike : toISO(dLike);
  const f = new Date(mondayOf(iso) + "T00:00:00");
  f.setDate(f.getDate() + 4);
  const fri = toISO(f);
  const ml = monthLast(iso);
  return iso <= ml && ml < fri ? ml : fri;
}

export function periodStart(end) {
  const mon = mondayOf(end);
  const first = end.slice(0, 8) + "01";
  return mon > first ? mon : first;
}

export function isPartWeek(end) {
  return !!end && (periodStart(end) !== mondayOf(end) || new Date(end + "T00:00:00").getDay() !== 5);
}

export function currentWeekEnding() {
  return periodEnd(new Date());
}

export function nextDay(iso) {
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() + 1);
  return toISO(d);
}

export function nextWorkday(iso) {
  let d = nextDay(iso);
  let dow = new Date(d + "T00:00:00").getDay();
  while (dow === 0 || dow === 6) {
    d = nextDay(d);
    dow = new Date(d + "T00:00:00").getDay();
  }
  return d;
}

export function weekdays(weekEnding) {
  const out = [];
  for (let cur = periodStart(weekEnding); cur <= weekEnding; cur = nextDay(cur)) {
    const dow = new Date(cur + "T00:00:00").getDay();
    if (dow !== 0 && dow !== 6) out.push(cur);
  }
  return out;
}

export function weekLabel(weekEnding) {
  if (!weekEnding) return "-";
  const d = new Date(weekEnding + "T00:00:00");
  if (isNaN(d)) return weekEnding;
  return "Week ending " + d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) + (isPartWeek(weekEnding) ? " (part week)" : "");
}

export function dayLabel(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr + "T00:00:00");
  return isNaN(d) ? "" : d.toLocaleDateString("en-GB", { weekday: "short" });
}

export function prettyDate(dateStr) {
  if (!dateStr) return "-";
  const d = new Date(dateStr + "T00:00:00");
  return isNaN(d) ? dateStr : d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
}

export function computeSummary(entries, weekEnding) {
  const s = { standard: 0, overtime: 0, leave: 0, dates_outside: 0 };
  const start = weekEnding ? periodStart(weekEnding) : null;
  (entries || []).forEach((e) => {
    s[TYPE_BUCKET[e.type] || "standard"] += parseFloat(e.hours) || 0;
    if (start && e.date && (e.date < start || e.date > weekEnding)) s.dates_outside += 1;
  });
  s.total_worked = s.standard + s.overtime;
  s.total_leave = s.leave;
  return s;
}

export function formatDate(iso) {
  if (!iso) return "-";
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

export function fmtHrs(n) {
  const v = parseFloat(n) || 0;
  return v % 1 === 0 ? String(v) : v.toFixed(1);
}


export function currentMonth() {
  return toISO(new Date()).slice(0, 7);
}

export function monthLabel(ym) {
  const [y, m] = (ym || "").split("-").map(Number);
  if (!y || !m) return ym || "";
  return new Date(y, m - 1, 1).toLocaleDateString("en-GB", { month: "long", year: "numeric" });
}

function lastWeekday(y, m0) {
  const d = new Date(y, m0 + 1, 0);
  while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() - 1);
  return toISO(d);
}

// "YYYY-MM" if this week contains its month's last working day, else null
export function monthEndOf(weekEnding) {
  const f = new Date((weekEnding || "") + "T00:00:00");
  if (Number.isNaN(f.getTime())) return null;
  const lw = lastWeekday(f.getFullYear(), f.getMonth());
  return lw >= periodStart(weekEnding) && lw <= weekEnding ? lw.slice(0, 7) : null;
}

// Months (YYYY-MM) a timesheet covers; older sheets may span two months
export function sheetMonths(s) {
  const ms = (s.entries || []).map((e) => (e.date || "").slice(0, 7)).filter(Boolean);
  return [...new Set([(s.week_ending || "").slice(0, 7), ...ms])].filter(Boolean);
}

// Group timesheets by month, newest first; older sheets spanning two months appear under both
export function groupByMonth(sheets) {
  const groups = {};
  sheets.forEach((s) => {
    sheetMonths(s).forEach((ym) => { (groups[ym] = groups[ym] || []).push(s); });
  });
  return Object.keys(groups).sort().reverse().map((ym) => ({ ym, label: monthLabel(ym), items: groups[ym] }));
}

